import { createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

import {
  AddEmploymentInput,
  AddEmploymentOldInput,
  AvailableFokParameterChoiceViewModel,
  EmploymentBoardDecisionLockViewModel,
  EmploymentReportStatusViewModel,
  EmploymentViewModel,
  EmploymentViewModelNew,
  GetEmploymentsSearchParams,
  GetExactDuplicateEmploymentSearchParam,
  GetPartialDuplicateEmploymentsSearchParam,
  RequestError,
  UpdateEmploymentInput,
  UpdateEmploymentOldInput,
} from '@fora-monorepo/employer-my-pages-common/types';
import { trackException } from '@fora-monorepo/webapp-analytics';

import { envConfig } from '../../config';
import {
  mockAddEmployment,
  mockAddEmploymentOld,
  mockBoardDecisionLocks,
  mockEmploymentReportsStatus,
  mockEmployments,
  mockExactDuplicateEmployment,
  mockPartialDuplicateEmployments,
  mockUpdateEmployment,
  mockUpdateEmploymentOld,
} from './mocks';
import { createThunkError } from '../utils';
import { mockEmploymentsOld } from './mocks/mockEmploymentsOld';
import { mockAvailableFokParameterChoices } from './mocks/mockAvailableFokParameterChoices';

export const getCurrentEmployments = createAsyncThunk('getCurrentEmployments', async (_, thunkAPI) => {
  if (envConfig.mockDataFrontend) {
    await new Promise(resolve => setTimeout(resolve, 500));
    return mockEmploymentsOld.default;
  }

  const body: GetEmploymentsSearchParams = {};

  return axios
    .post(`${envConfig.bffBaseUri}/employments`, body)
    .then(res => {
      return res.data as EmploymentViewModel[];
    })
    .catch(err => {
      trackException(err);
      return thunkAPI.rejectWithValue(createThunkError('Could not get employments', err));
    });
});

export const getCurrentEmploymentsNew = createAsyncThunk('getCurrentEmploymentsNew', async (_, thunkAPI) => {
  if (envConfig.mockDataFrontend) {
    await new Promise(resolve => setTimeout(resolve, 500));
    return mockEmployments.default;
  }

  const body: GetEmploymentsSearchParams = {};

  return axios
    .post(`${envConfig.bffBaseUri}/employments`, body)
    .then(res => {
      return res.data as EmploymentViewModelNew[];
    })
    .catch(err => {
      trackException(err);
      return thunkAPI.rejectWithValue(createThunkError('Could not get employments', err));
    });
});

export const getExactDuplicateEmployments = createAsyncThunk(
  'getExactDuplicateEmployments',
  async (getEmploymentInput: GetExactDuplicateEmploymentSearchParam, thunkAPI) => {
    if (envConfig.mockDataFrontend) {
      await new Promise(resolve => setTimeout(resolve, 500));
      if (getEmploymentInput.employeeId === '19880229-2394') {
        return mockExactDuplicateEmployment.match;
      }
      return mockExactDuplicateEmployment.noMatch;
    }

    return axios
      .post(`${envConfig.bffBaseUri}/employments/exact-duplicate`, getEmploymentInput)
      .then(res => {
        return res.data as EmploymentViewModel | null;
      })
      .catch(err => {
        trackException(err);
        return thunkAPI.rejectWithValue(createThunkError('Could not get exact duplicate', err));
      });
  }
);

export const getEmployerHasEmployments = createAsyncThunk('getEmployerHasEmployments', async (_, thunkAPI) => {
  if (envConfig.mockDataFrontend) {
    return { employerHasEmployments: true };
  }

  return axios
    .get(`${envConfig.bffBaseUri}/employments/has-employments`)
    .then(res => {
      return res.data as { employerHasEmployments: boolean };
    })
    .catch(err => {
      trackException(err);
      return thunkAPI.rejectWithValue(createThunkError('Could not get has-employments', err));
    });
});

export const getPartialDuplicateEmployments = createAsyncThunk(
  'getPartialDuplicateEmployments',
  async (getEmploymentInput: GetPartialDuplicateEmploymentsSearchParam, thunkAPI) => {
    if (envConfig.mockDataFrontend) {
      await new Promise(resolve => setTimeout(resolve, 500));
      if (getEmploymentInput.employeeId === '20010369-2380') {
        return mockPartialDuplicateEmployments.match;
      }
      return mockPartialDuplicateEmployments.noMatch;
    }

    return axios
      .post(`${envConfig.bffBaseUri}/employments/partial-duplicates`, getEmploymentInput)
      .then(res => {
        return res.data as EmploymentViewModel[] | EmploymentViewModelNew[];
      })
      .catch(err => {
        trackException(err);
        return thunkAPI.rejectWithValue(createThunkError('Could not get partial duplicates', err));
      });
  }
);

export const addEmployment = createAsyncThunk<
  EmploymentViewModelNew,
  AddEmploymentInput,
  { rejectValue: RequestError }
>('addEmployment', async (addEmploymentInput: AddEmploymentInput, thunkAPI) => {
  if (envConfig.mockDataFrontend) {
    await new Promise(resolve => setTimeout(resolve, 1000));
    const addedEmployment = mockAddEmployment(addEmploymentInput);
    return addedEmployment;
  }

  return axios
    .post(`${envConfig.bffBaseUri}/employment-emp-periods`, addEmploymentInput)
    .then(res => {
      return res.data as EmploymentViewModelNew;
    })
    .catch(err => {
      trackException(err);
      return thunkAPI.rejectWithValue(createThunkError('Could not add employment', err));
    });
});
export const addEmploymentOld = createAsyncThunk<
  EmploymentViewModel,
  AddEmploymentOldInput,
  { rejectValue: RequestError }
>('addEmploymentOld', async (addEmploymentInput: AddEmploymentOldInput, thunkAPI) => {
  if (envConfig.mockDataFrontend) {
    await new Promise(resolve => setTimeout(resolve, 1000));
    const addedEmployment = mockAddEmploymentOld(addEmploymentInput);
    return addedEmployment;
  }

  return axios
    .post(`${envConfig.bffBaseUri}/employment`, addEmploymentInput)
    .then(res => {
      return res.data as EmploymentViewModel;
    })
    .catch(err => {
      trackException(err);
      return thunkAPI.rejectWithValue(createThunkError('Could not add employment', err));
    });
});
export const updateEmployment = createAsyncThunk<
  EmploymentViewModelNew,
  { unchangedEmployment: EmploymentViewModelNew; updateEmploymentInput: UpdateEmploymentInput },
  { rejectValue: RequestError }
>(
  'updateEmployment',
  async (
    {
      unchangedEmployment,
      updateEmploymentInput,
    }: { unchangedEmployment: EmploymentViewModelNew; updateEmploymentInput: UpdateEmploymentInput },
    thunkAPI
  ) => {
    if (envConfig.mockDataFrontend) {
      await new Promise(resolve => setTimeout(resolve, 1000));
      return mockUpdateEmployment(unchangedEmployment, updateEmploymentInput);
    }

    return axios
      .put(`${envConfig.bffBaseUri}/employment-emp-periods/${unchangedEmployment.policyNumber}`, {
        updateEmploymentInput,
      })
      .then(res => {
        return res.data as EmploymentViewModelNew;
      })
      .catch(err => {
        trackException(err);
        return thunkAPI.rejectWithValue(createThunkError('Could not update employment', err));
      });
  }
);
export const updateEmploymentOld = createAsyncThunk<
  EmploymentViewModel,
  { unchangedEmployment: EmploymentViewModel; updateEmploymentInput: UpdateEmploymentOldInput },
  { rejectValue: RequestError }
>(
  'updateEmploymentOld',
  async (
    {
      unchangedEmployment,
      updateEmploymentInput,
    }: { unchangedEmployment: EmploymentViewModel; updateEmploymentInput: UpdateEmploymentOldInput },
    thunkAPI
  ) => {
    if (envConfig.mockDataFrontend) {
      await new Promise(resolve => setTimeout(resolve, 1000));
      return mockUpdateEmploymentOld(unchangedEmployment, updateEmploymentInput);
    }

    return axios
      .put(`${envConfig.bffBaseUri}/employment/${unchangedEmployment.policyNumber}`, { updateEmploymentInput })
      .then(res => {
        return res.data as EmploymentViewModel;
      })
      .catch(err => {
        trackException(err);
        return thunkAPI.rejectWithValue(createThunkError('Could not update employment', err));
      });
  }
);

export const getEmploymentReportStatus = createAsyncThunk('getEmploymentReportStatus', async (_, thunkAPI) => {
  if (envConfig.mockDataFrontend) {
    return mockEmploymentReportsStatus;
  }

  return axios
    .get(`${envConfig.bffBaseUri}/employments/report-status`)
    .then(res => {
      return res.data as EmploymentReportStatusViewModel;
    })
    .catch(err => {
      trackException(err);
      return thunkAPI.rejectWithValue(createThunkError('Could not get employment report status', err));
    });
});

export const getEmploymentBoardDecisionLocks = createAsyncThunk(
  'getEmploymentBoardDecisionLocks',
  async (individualPolicyNumber: string, thunkAPI) => {
    if (envConfig.mockDataFrontend) {
      return mockBoardDecisionLocks.noLock;
    }

    return axios
      .get(`${envConfig.bffBaseUri}/employments/board-decision-locks/${individualPolicyNumber}`)
      .then(res => {
        return res.data as EmploymentBoardDecisionLockViewModel[];
      })
      .catch(err => {
        trackException(err);
        return thunkAPI.rejectWithValue(createThunkError('Could not get employment board decision locks', err));
      });
  }
);

export const geAvailableFokParameterChoices = createAsyncThunk(
  'geAvailableFokParameterChoices',
  async (individualPolicyNumber: string, thunkAPI) => {
    if (envConfig.mockDataFrontend) {
      return mockAvailableFokParameterChoices;
    }

    return axios
      .get(`${envConfig.bffBaseUri}/employments/available-parameters/${individualPolicyNumber}`)
      .then(res => {
        return res.data as AvailableFokParameterChoiceViewModel[];
      })
      .catch(err => {
        trackException(err);
        return thunkAPI.rejectWithValue(createThunkError('Could not get available fok parameter choices', err));
      });
  }
);
